/**
 * 진화 기록의 반출 · 반입 · 병합(docs/features/24-experience-evolution.md §8).
 *
 * 내부망·스탠드얼론에서는 파일이 PC 를 옮기거나 남의 경험을 받는 유일한 길이다.
 *   · 내보내기 — 고른 섹션만. 연결·테이블 이름은 기본으로 싣지 않고, 싣기로 하면 암호가 필수다. 암호 없이 나가는 파일은 가드레일의 내보내기 검사를 지난다.
 *   · 가져오기 — 파일은 **미적용으로 도착**한다. 내 것과 견준 표에서 고른 것만(고쳐서) 들어오고, 충돌의 기본은 내 것이다.
 *     남이 쓴 선호 문장은 AI 요청에 실리는 지시가 되므로 자동 적용이 없다. 한 번의 병합은 통째로 되돌릴 수 있다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiDownload, FiUpload, FiArrowLeft, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { saveTextFile } from '../../utils/desktopFile';
import { guardExport } from '../../utils/guardrailGate';
import * as guardrailApi from '../../services/guardrail';
import * as api from '../../services/experience';

/** 내보낼 수 있는 섹션 — 백엔드 `experience/exchange.js` 의 `SECTION_IDS` 와 같아야 한다 */
export const EXPORT_SECTIONS = ['userPref', 'strategy', 'discarded', 'tuningGuides'];
const MIN_PASSPHRASE = 8;

export function ExportPanel({ onBack }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(() => new Set(EXPORT_SECTIONS));
  const [includeEnv, setIncludeEnv] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const needPass = includeEnv;
  const passOk = !passphrase ? !needPass : passphrase.length >= MIN_PASSPHRASE;
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.exportExperience({ sections: [...picked], includeEnv, passphrase: passphrase || undefined, label });
      let text = JSON.stringify(r.file, null, 2);
      // 암호로 잠그지 않은 파일은 그대로 읽힌다 — 금지 항목이 있으면 빼기·치환·그대로를 묻는다(가드레일 4)
      if (!r.encrypted) {
        const g = await guardExport(text, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
        if (!g) return;
        if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
        text = g.text;
      }
      const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const saved = await saveTextFile({ fileName: `livesql-experience-${day}.json`, content: text, kind: 'project', saveAs: true });
      if (saved && saved.ok === false && !saved.canceled) { toast.error(saved.detail || saved.reason || t('common:evo.xch.exportFailed')); return; }
      if (saved && saved.canceled) return;
      toast.success(t('common:evo.xch.exported', { prefs: (r.counts && r.counts.userPref) || 0, guides: (r.counts && r.counts.tuningGuides) || 0 }));
      onBack();
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };

  return (
    <div className="exp-xch">
      <div className="exp-xchHead">
        <button type="button" className="app-btn" onClick={onBack}><FiArrowLeft size={12} /> {t('common:evo.xch.back')}</button>
        <strong>{t('common:evo.xch.exportTitle')}</strong>
      </div>
      <div className="exp-intro">{t('common:evo.xch.exportIntro')}</div>
      <div className="exp-xchRows">
        {EXPORT_SECTIONS.map(id => (
          <label key={id} className="exp-check"><input type="checkbox" checked={picked.has(id)} onChange={() => toggle(id)} /> {t(`common:evo.xch.section.${id}`)}
            <span className="exp-propHint">{t(`common:evo.xch.sectionHint.${id}`)}</span></label>
        ))}
      </div>
      <label className="exp-check"><input type="checkbox" checked={includeEnv} onChange={e => setIncludeEnv(e.target.checked)} /> {t('common:evo.xch.includeEnv')}
        <span className="exp-propHint">{t('common:evo.xch.includeEnvHint')}</span></label>
      <div className="exp-add">
        <input className="exp-input" type="password" autoComplete="new-password" value={passphrase} placeholder={t(needPass ? 'common:evo.xch.passRequired' : 'common:evo.xch.passOptional', { min: MIN_PASSPHRASE })}
          aria-label={t('common:evo.xch.pass')} onChange={e => setPassphrase(e.target.value)} />
        <input className="exp-input" value={label} maxLength={120} placeholder={t('common:evo.xch.labelPh')} aria-label={t('common:evo.xch.labelPh')} onChange={e => setLabel(e.target.value)} />
        <button type="button" className="app-btn app-btn-primary" disabled={busy || !picked.size || !passOk} onClick={run}><FiDownload size={12} /> {t('common:evo.xch.export')}</button>
      </div>
    </div>
  );
}
ExportPanel.propTypes = { onBack: PropTypes.func.isRequired };

/** 파일 고르기 → 층으로 들이기. 암호 파일이면 암호를 묻는다(같은 파일로 다시 보낸다) */
export function ImportBar({ onImported }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);     // 암호를 기다리는 파일 { raw, name }
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (raw, name, pass) => {
    setBusy(true);
    try {
      const r = await api.importFile({ raw, passphrase: pass || undefined, label: name });
      setPending(null); setPassphrase('');
      const cut = Object.values(r.truncated || {}).reduce((n, x) => n + x, 0);
      if (cut) toast.warning(t('common:evo.xch.truncated', { count: cut }));
      onImported(r.layer);
    } catch (err) {
      const code = err && err.response && err.response.data && err.response.data.errorType;
      if (code === 'EXP_IMPORT_NEEDS_PASSPHRASE') setPending({ raw, name });
      else toast.error(formatApiError(err, t));
    } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    const raw = await f.text();
    send(raw, f.name.replace(/\.json$/i, ''), '');
  };

  return (
    <span className="exp-importBar">
      <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={onFile} data-testid="exp-file" />
      <button type="button" className="app-btn" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}><FiUpload size={12} /> {t('common:evo.xch.import')}</button>
      {pending ? (
        <span className="exp-editRow">
          <input className="exp-input" type="password" autoComplete="off" value={passphrase} placeholder={t('common:evo.xch.passAsk')} aria-label={t('common:evo.xch.passAsk')}
            onChange={e => setPassphrase(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && passphrase) send(pending.raw, pending.name, passphrase); }} />
          <button type="button" className="app-btn app-btn-primary" disabled={busy || !passphrase} onClick={() => send(pending.raw, pending.name, passphrase)}>{t('common:evo.xch.open')}</button>
          <button type="button" className="app-btn" onClick={() => { setPending(null); setPassphrase(''); }}>{t('common:action.cancel')}</button>
        </span>
      ) : null}
    </span>
  );
}
ImportBar.propTypes = { onImported: PropTypes.func.isRequired };

/** 가져온 자료의 층 목록 — 견줘 보기 · 이 출처 모두 끄기/켜기 · 되돌리기 · 지우기 */
export function LayerList({ layers, busy, onOpen, onToggle, onUndo, onRemove }) {
  const { t } = useTranslation();
  if (!(layers || []).length) return null;
  return (
    <section className="exp-group">
      <h4 className="exp-groupTitle">{t('common:evo.xch.layers')} <span className="exp-count">{layers.length}</span></h4>
      <div className="exp-groupHint">{t('common:evo.xch.layersHint')}</div>
      <ul className="exp-list">
        {layers.map(l => (
          <li key={l.id} className="exp-item">
            <div className="exp-main">
              <span className="exp-titleText">{l.label}</span>
              <span className="exp-meta">
                <span className={`exp-chip exp-layer-${l.status}`}>{t(`common:evo.xch.status.${l.status}`, { defaultValue: l.status })}</span>
                <span className="exp-ev">{storedTime(l.createdAt, 'date')}{l.applied ? ` · ${t('common:evo.xch.appliedCount', { prefs: l.applied.items, guides: l.applied.guides })}` : ''}</span>
              </span>
            </div>
            <div className="exp-actions">
              {l.status !== 'merged' && <button type="button" className="app-btn app-btn-primary" disabled={busy} onClick={() => onOpen(l)}>{t('common:evo.xch.compare')}</button>}
              {l.status === 'merged' && <button type="button" className="app-btn" disabled={busy} onClick={() => onToggle(l, false)}>{t('common:evo.xch.allOff')}</button>}
              {l.status === 'merged' && <button type="button" className="app-btn" disabled={busy} onClick={() => onToggle(l, true)}>{t('common:evo.xch.allOn')}</button>}
              {l.status === 'merged' && <button type="button" className="app-btn" disabled={busy} onClick={() => onUndo(l)}><FiRotateCcw size={11} /> {t('common:evo.xch.undo')}</button>}
              {l.status !== 'merged' && <button type="button" className="app-btn app-btn-danger" disabled={busy} onClick={() => onRemove(l)} title={t('common:action.delete')} aria-label={t('common:action.delete')}><FiTrash2 size={11} /></button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
LayerList.propTypes = {
  layers: PropTypes.array, busy: PropTypes.bool, onOpen: PropTypes.func.isRequired, onToggle: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired, onRemove: PropTypes.func.isRequired,
};

/**
 * 병합 화면 — 내 것과 견준 표에서 고른다. 확정(「합치기」) 전에는 아무것도 적용되지 않는다.
 * 선호는 글을 고쳐서 받을 수 있고, 연결에 묶인 것은 내 연결을 골라야 들어온다. 검증 횟수·남의 폐기 목록은 참고로만 보인다.
 */
export function MergeView({ layerId, onBack, onDone }) {
  const { t } = useTranslation();
  const connections = useSelector(st => ((st.schema && st.schema.connections) || []).filter(c => c.dbType !== 'sqlite'));
  const [pv, setPv] = useState(null);
  const [prefs, setPrefs] = useState({});       // idx → { take, text, connectionId }
  const [guides, setGuides] = useState({});     // idx → { take }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setPv(null);
    api.previewImport(layerId).then((r) => {
      if (!alive) return;
      setPv(r);
      setPrefs(Object.fromEntries(r.sections.userPref.map(p => [p.idx, { take: !!p.take, text: p.text, connectionId: '' }])));
      setGuides(Object.fromEntries(r.sections.tuningGuides.map(g => [g.idx, { take: !!g.take }])));
    }).catch((err) => { if (alive) { toast.error(formatApiError(err, t)); onBack(); } });
    return () => { alive = false; };
  }, [layerId, t, onBack]);

  const setPref = useCallback((idx, patch) => setPrefs(s => ({ ...s, [idx]: { ...s[idx], ...patch } })), []);
  const count = useMemo(() => Object.values(prefs).filter(x => x.take).length + Object.values(guides).filter(x => x.take).length, [prefs, guides]);
  const setAll = (take) => setPrefs(s => Object.fromEntries(Object.entries(s).map(([k, v]) => {
    const p = pv.sections.userPref.find(x => String(x.idx) === String(k));
    // 「모두 받기」 도 받을 수 없는 것(이미 있음·길이)은 건드리지 않는다
    return [k, { ...v, take: take && p && !['same', 'tooLong'].includes(p.state) }];
  })));

  const apply = async () => {
    setBusy(true);
    try {
      const choices = {
        userPref: Object.fromEntries(Object.entries(prefs).filter(([, v]) => v.take).map(([k, v]) => [k, { take: true, text: v.text, connectionId: v.connectionId || undefined }])),
        tuningGuides: Object.fromEntries(Object.entries(guides).filter(([, v]) => v.take).map(([k]) => [k, { take: true }])),
      };
      const r = await api.applyImport(layerId, choices);
      toast.success(t('common:evo.xch.merged', { prefs: r.taken.prefs, guides: r.taken.guides }));
      if ((r.skipped || []).length) toast.warning(t('common:evo.xch.skipped', { count: r.skipped.length }));
      onDone();
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };

  if (!pv) return <div className="exp-empty">{t('common:state.loading')}</div>;
  const s = pv.sections;
  return (
    <div className="exp-xch">
      <div className="exp-xchHead">
        <button type="button" className="app-btn" onClick={onBack}><FiArrowLeft size={12} /> {t('common:evo.xch.back')}</button>
        <strong>{t('common:evo.xch.mergeTitle', { label: pv.layer.label })}</strong>
      </div>
      <div className="exp-intro">{t('common:evo.xch.mergeIntro')}</div>

      <section className="exp-group">
        <h4 className="exp-groupTitle">{t('common:evo.xch.section.userPref')} <span className="exp-count">{s.userPref.length}</span>
          {s.userPref.length > 0 && <span className="exp-bulk"><button type="button" className="app-btn" onClick={() => setAll(true)}>{t('common:evo.xch.takeAll')}</button><button type="button" className="app-btn" onClick={() => setAll(false)}>{t('common:evo.xch.takeNone')}</button></span>}
        </h4>
        <ul className="exp-list">
          {s.userPref.map((p) => {
            const c = prefs[p.idx] || {};
            const blocked = ['same', 'tooLong'].includes(p.state);
            return (
              <li key={p.idx} className={`exp-item exp-merge-${p.state}`}>
                <input type="checkbox" checked={!!c.take} disabled={blocked} aria-label={p.text} onChange={e => setPref(p.idx, { take: e.target.checked })} />
                <div className="exp-main">
                  <input className="exp-input" value={c.text === undefined ? p.text : c.text} disabled={blocked || !c.take} aria-label={t('common:evo.edit')} onChange={e => setPref(p.idx, { text: e.target.value })} />
                  <span className="exp-meta">
                    <span className={`exp-chip exp-state-${p.state}`}>{t(`common:evo.xch.state.${p.state}`)}</span>
                    <span className="exp-chip">{p.task ? t(`common:evo.task.${p.task}`, { defaultValue: p.task }) : t('common:evo.scope.all')}</span>
                    {p.connection ? (
                      <select className="exp-select" value={c.connectionId || ''} disabled={!c.take} aria-label={t('common:evo.xch.pickConn')} onChange={e => setPref(p.idx, { connectionId: e.target.value })}>
                        <option value="">{t('common:evo.xch.pickConn')}{p.connection.name ? ` (${p.connection.name})` : ` (${p.connection.ref})`}</option>
                        {connections.map(cn => <option key={cn.id} value={cn.id}>{cn.name || cn.id}</option>)}
                      </select>
                    ) : null}
                  </span>
                </div>
              </li>
            );
          })}
          {!s.userPref.length && <li className="exp-empty">{t('common:evo.xch.none')}</li>}
        </ul>
      </section>

      {s.tuningGuides.length > 0 && (
        <section className="exp-group">
          <h4 className="exp-groupTitle">{t('common:evo.xch.section.tuningGuides')} <span className="exp-count">{s.tuningGuides.length}</span></h4>
          <div className="exp-groupHint">{t('common:evo.xch.guidesHint')}</div>
          <ul className="exp-list">
            {s.tuningGuides.map(g => (
              <li key={g.idx} className="exp-item">
                <input type="checkbox" checked={!!(guides[g.idx] && guides[g.idx].take)} aria-label={g.title} onChange={e => setGuides(x => ({ ...x, [g.idx]: { take: e.target.checked } }))} />
                <div className="exp-main">
                  <span className="exp-titleText">{g.title}</span>
                  <span className="exp-meta">
                    <span className={`exp-chip exp-state-${g.state}`}>{t(`common:evo.xch.state.${g.state}`)}</span>
                    <span className="exp-ev">{t('common:evo.xch.guideRules', { count: g.rules })}{g.hasPriority ? ` · ${t('common:evo.xch.guidePriority')}` : ''}</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(s.strategy.length > 0 || s.discarded.length > 0) && (
        <section className="exp-group">
          <h4 className="exp-groupTitle">{t('common:evo.xch.reference')}</h4>
          <div className="exp-groupHint">{t('common:evo.xch.referenceHint')}</div>
          <ul className="exp-propList">
            {s.strategy.map(x => <li key={`s${x.idx}`}>{x.label}{x.connection ? ` · ${x.connection.name || x.connection.ref}` : ''} — {t('common:evo.ev.fix', { tried: x.evidence.tried || 0, improved: x.evidence.improved || 0, failed: (x.evidence.failed || 0) + (x.evidence.invalid || 0) })}</li>)}
            {s.discarded.map(x => <li key={`d${x.idx}`}>{t('common:evo.xch.theirDiscarded')}: {x.text}</li>)}
          </ul>
        </section>
      )}

      <div className="exp-propActions exp-xchFoot">
        <button type="button" className="app-btn app-btn-primary" disabled={busy || !count} onClick={apply}>{t('common:evo.xch.merge', { count })}</button>
        <button type="button" className="app-btn" onClick={onBack}>{t('common:evo.xch.later')}</button>
      </div>
    </div>
  );
}
MergeView.propTypes = { layerId: PropTypes.string.isRequired, onBack: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };
